import CampaignGanttChart from '@/components/CampaignGanttChart'
import PhishCampaignSmsDetails from '@/components/PhishCampaignSmsDetails'
import TableTabButton from '@/components/TableTabButton'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import AddCircle from '@mui/icons-material/AddCircle'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Close from '@mui/icons-material/HighlightOff'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
// import { ReactComponent as DeleteIcon } from '@/assets/trash_bin.svg'
import usePermissions from '@/hooks/usePermissions'
import PhishCampaignSmsChildDetails from '@/components/PhishCampaignSmsChildDetails'

const mapStateToProps = ({ phishCampaign, loading }) => ({
  phishSmsCampaignList: phishCampaign.phishSmsCampaignList,
  phishSmsCampaignsStatusTotal: phishCampaign.phishSmsCampaignsStatusTotal,
  phishSmsCampaignChildren: phishCampaign.phishSmsCampaignChildren,
  isLoading: loading['phishCampaign/getPhishSmsCampaigns'],
  isDetailsLoading: loading['phishCampaign/getPhishSmsCampaignDetails'],
  deleteLoading: loading['phishCampaign/deletePhishCampaign'],
})

const fieldMap = {
  campaignMainName: 'CAMP_NAME',
  createdTime: 'CREATED_TIME',
  sendSuc: 'SENT',
  remainingDays: 'REMAINING_DAYS',
  phishPercent: 'PHISHING_PERCENT',
  startTime: 'CAMP_START',
  status: 'STATUS',
}

const btnProps = {
  size: 'small',
  sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
}

const iconProps = {
  sx: {
    padding: 0.5,
  },
}

const PhishCampaignSmsDashboard = ({
  dispatch,
  phishSmsCampaignList,
  phishSmsCampaignsStatusTotal,
  phishSmsCampaignChildren,
  isLoading,
  isDetailsLoading,
  deleteLoading,
}) => {
  const { data, paging = {} } = phishSmsCampaignList || {}

  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { phishCampaignId, childId } = params
  const [deleteCampData, setDeleteCampData] = useState()
  const [filterDateRange, setFilterDateRange] = useState()
  const [search, setSearch] = useState('')
  const [searchStatus, setSearchStatus] = useState('')
  const [sort, setSort] = useState([{ field: 'createdTime', sort: 'desc' }])
  const [showChart, setShowChart] = useState(true)
  const [isCustomRange, setIsCustomRange] = useState(true)
  const [selectedDateOpt, setSelectedDateOpt] = useState(6)
  const [runTypeOpt, setRunTypeOpt] = useState(['main', 'pre'])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [expandedId, setExpandedId] = useState()
  const [showDetails, setShowDetails] = useState(false)
  const [showChildDetails, setShowChildDetails] = useState(false)
  const form = useRef()
  const detailsRef = useRef()

  const canDelete = usePermissions('emailCampaign_delete')
  const canCreate = usePermissions('emailCampaign_create')
  const [companyId, setCompanyId] = useState(null)

  useEffect(() => {
    dispatch({
      type: 'account/getTestCompany',
      successCallback: testCompanies => {
        if (Array.isArray(testCompanies.data) && testCompanies.data.length > 0) {
          setCompanyId(testCompanies.data[0].companyId)
        }
      },
    })
  }, [dispatch])

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const fetchData = useCallback(() => {
    if (filterDateRange) {
      const payload = {
        searchCampInitialTime: moment(filterDateRange.startDate).toDate(),
        searchCampEndTime: moment(filterDateRange.endDate).toDate(),
        searchCampName: search,
        sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
        status: searchStatus,
        runType: runTypeOpt.join(','),
        pageIndex: page,
        pageSize,
      }

      dispatch({
        type: 'phishCampaign/getPhishSmsCampaigns',
        payload,
      })

      dispatch({
        type: 'phishCampaign/getPhishSmsCampaignsStatusTotal',
      })
    }
  }, [dispatch, filterDateRange, search, searchStatus, runTypeOpt, sort, page, pageSize])

  const fetchChildren = id => {
    if (filterDateRange) {
      const payload = {
        searchCampInitialTime: moment(filterDateRange.startDate).toDate(),
        searchCampEndTime: moment(filterDateRange.endDate).toDate(),
        sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
        pageIndex: 0,
        pageSize: 10,
        campaignId: id,
      }

      dispatch({
        type: 'phishCampaign/getPhishSmsCampaignChildren',
        payload,
      })
    }
  }

  const handleRowClick = d => {
    navigate(`/phish_campaign_sms/${d.campaignMainId}`)
    fetchData()
  }

  const handleChildRowClick = d => {
    navigate(`/phish_campaign_sms/${d.campaignMainId}/${d.campaignId}`)
    fetchData()
  }

  const handleExpand = d => {
    const { campaignMainId: expandId } = d
    if (expandId === expandedId) {
      setExpandedId()
    } else {
      setExpandedId(expandId)
      fetchChildren(expandId)
    }
  }

  const ChildrenComponent = ({ mainId, field, valueFormatter, render }) => {
    if (mainId.toString() === expandedId?.toString()) {
      return (
        <Stack height="100%" width={'100%'}>
          {phishSmsCampaignChildren?.[mainId]?.map((child, index) => (
            <Box
              key={`${mainId}_child_${index + 1}`}
              p={1}
              width={'100%'}
              color={theme => theme.palette.accent_60.main}
            >
              {render?.(child) || (
                // <Tooltip title={child[field]} placement="top-end">
                <Typography
                  pl={2}
                  sx={{ width: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {valueFormatter?.(child[field]) || child[field]}
                </Typography>
                // </Tooltip>
              )}
            </Box>
          ))}
        </Stack>
      )
    }

    return null
  }

  const cols = [
    {
      field: 'campaignMainName',
      headerName: t('general.campaign_name'),
      width: 200,
      renderCell: ({ value, row }) => (
        <Stack width={'100%'}>
          <Stack direction={'row'}>
            <Button
              sx={{ flex: 1, padding: 0, justifyContent: 'flex-start', width: 'auto' }}
              variant="text"
              onClick={() => {
                handleRowClick(row)
              }}
            >
              <Stack width={'100%'} flexDirection={'row'} spacing={1} alignItems={'center'}>
                <Tooltip title={value} placement="top-end">
                  <Typography
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {value}
                  </Typography>
                </Tooltip>
              </Stack>
            </Button>

            {row.children === 1 && (
              <IconButton
                component="div"
                size="small"
                sx={{ marginTop: '0 !important' }}
                onClick={e => {
                  e.stopPropagation()
                  handleExpand(row)
                }}
              >
                {row.campaignMainId.toString() === expandedId?.toString() ? (
                  <ArrowDropUp />
                ) : (
                  <ArrowDropDown />
                )}
              </IconButton>
            )}
          </Stack>

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="campaignName"
            render={child => (
              <Button
                sx={{ padding: 0, justifyContent: 'flex-start', width: 'auto', maxWidth: '100%' }}
                variant="text"
                color="accent_60"
                onClick={() => {
                  handleChildRowClick({ ...row, ...child })
                }}
              >
                <Tooltip title={child.campaignName} placement="top-end">
                  <Typography
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {child.campaignName}
                  </Typography>
                </Tooltip>
              </Button>
            )}
          />
        </Stack>
      ),
    },
    {
      field: 'frequency',
      headerName: t('general.play_num'),
      width: 80,
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value || '-'}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="frequency"
            valueFormatter={val => val || '-'}
          />
        </Stack>
      ),
    },
    {
      field: 'sendSuc',
      headerName: t('general.sent'),
      width: 150,
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value || '-'}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="sendSuc"
            valueFormatter={val => val || '-'}
          />
        </Stack>
      ),
    },
    {
      field: 'remainingDays',
      headerName: t('general.remaining_days'),
      width: 130,
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value || '-'}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="remainingDays"
            valueFormatter={val => val || '-'}
          />
        </Stack>
      ),
    },
    {
      field: 'phishPercent',
      headerName: t('general.phish_percent'),
      width: 100,
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {`${value}%`}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="phishPercent"
            valueFormatter={val => `${val}%`}
          />
        </Stack>
      ),
    },
    {
      field: 'createdTime',
      headerName: t('general.create_time'),
      width: 200,
      valueFormatter: ({ value }) => moment(value).format(DATE_TIME_FORMAT),
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {moment(value).format(DATE_TIME_FORMAT)}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="createdTime"
            valueFormatter={val => moment(val).format(DATE_TIME_FORMAT)}
          />
        </Stack>
      ),
    },
    {
      field: 'startTime',
      headerName: t('general.start_time'),
      width: 200,
      valueFormatter: ({ value }) => moment(value).format(DATE_TIME_FORMAT),
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {moment(value).format(DATE_TIME_FORMAT)}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="startTime"
            valueFormatter={val => moment(val).format(DATE_TIME_FORMAT)}
          />
        </Stack>
      ),
    },
    {
      field: 'endTime',
      headerName: t('general.end_time'),
      width: 200,
      valueFormatter: ({ value }) => moment(value).format(DATE_TIME_FORMAT),
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {moment(value).format(DATE_TIME_FORMAT)}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="endTime"
            valueFormatter={val => moment(val).format(DATE_TIME_FORMAT)}
          />
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 80,
      valueFormatter: ({ value }) => t(`status.${value}`),
      renderCell: ({ value, row }) => (
        <Stack height="100%" width={'100%'}>
          {/* <Tooltip title={value} placement="top-end"> */}
          <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t(`status.${value}`)}
          </Typography>
          {/* </Tooltip> */}

          <ChildrenComponent
            mainId={row.campaignMainId}
            field="status"
            valueFormatter={val => t(`status.${val}`)}
          />
        </Stack>
      ),
    },
    {
      field: 'delete',
      headerName: t('general.delete'),
      width: 50,
      hidden: !canDelete,
      renderCell: ({ row }) => {
        const disabled = moment().isBetween(
          moment(row.startTime).endOf('time'),
          moment(row.endTime).endOf('time'),
        )

        return (
          <Stack height="100%" width={'100%'}>
            <Tooltip
              title={disabled ? t('tooltips.cannot_delete_start_camp') : ''}
              sx={{ visibility: false }}
            >
              <Box p={1} visibility={row.children === 1 ? 'hidden' : 'visible'}>
                <IconButton
                  disabled={disabled}
                  {...btnProps}
                  onClick={() => {
                    setDeleteCampData(row)
                  }}
                >
                  <Close {...iconProps} />
                </IconButton>
              </Box>
            </Tooltip>

            <ChildrenComponent
              mainId={row.campaignMainId}
              render={child => {
                const childDisabled = moment().isBetween(
                  moment(child.startTime).endOf('time'),
                  moment(child.endTime).endOf('time'),
                )

                return (
                  <Tooltip title={childDisabled ? t('tooltips.cannot_delete_start_camp') : ''}>
                    <Box>
                      <IconButton
                        disabled={childDisabled}
                        {...btnProps}
                        onClick={() => {
                          setDeleteCampData(child)
                        }}
                      >
                        <Close {...iconProps} />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )
              }}
            />
          </Stack>
        )
      },
    },
  ]

  const resetPage = () => {
    setPage(0)
  }

  const fetchCampaignDetails = useCallback(() => {
    if (phishCampaignId) {
      dispatch({
        type: 'phishCampaign/getPhishSmsCampaignDetails',
        payload: { campaignId: phishCampaignId },
        successCallback: () => {
          // setTimeout(() => {
          //   detailsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
          // }, 200)
        },
      })

      fetchData()
      fetchChildren(phishCampaignId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phishCampaignId])

  const fetchCampaignChildDetails = useCallback(() => {
    if (childId) {
      dispatch({
        type: 'phishCampaign/getPhishSmsCampaignChildDetails',
        payload: { campaignId: childId },
        // successCallback: response => {
        // setTimeout(() => {
        //   detailsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
        // }, 200)
        // },
      })

      fetchData()
      fetchChildren(childId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId])

  const deletePhishCamp = () => {
    if (deleteCampData) {
      const { campaignId, campaignMainId, labelName } = deleteCampData
      dispatch({
        type: 'phishCampaign/deletePhishCampaign',
        payload: { phishCampId: campaignId || campaignMainId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: labelName,
              }),
              severity: 'success',
            },
          })
          setDeleteCampData()
          fetchData()
          fetchChildren(expandedId)
        },
        errorCallback: () => {
          setDeleteCampData()
        },
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [sort, page, filterDateRange, search, searchStatus, runTypeOpt, pageSize, fetchData])

  useEffect(() => {
    resetPage()
  }, [filterDateRange, search, searchStatus, runTypeOpt])

  useEffect(() => {
    if (selectedDateOpt !== '0') {
      const monthValue = Math.floor(selectedDateOpt / 2)
      const remainder = (selectedDateOpt / 2) % 1
      const dayValue = 30 * remainder

      const startDate = moment().subtract(monthValue, 'month').subtract(dayValue, 'day').toDate()
      const endDate = moment().add(monthValue, 'month').add(dayValue, 'day').toDate()

      form.current?.setValue?.('dateRangeFilter', {
        startDate,
        endDate,
      })

      setFilterDateRange({
        startDate,
        endDate,
      })

      setIsCustomRange(false)
    } else {
      setIsCustomRange(true)
    }
  }, [selectedDateOpt])

  useEffect(() => {
    if (phishCampaignId) {
      fetchCampaignDetails()
      // setTimeout(() => {
      //   detailsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
      // }, 200)
    }
  }, [phishCampaignId, fetchCampaignDetails])

  useEffect(() => {
    if (childId) {
      fetchCampaignChildDetails()
      // setTimeout(() => {
      //   detailsRef.current?.scrollIntoView?.({ behavior: 'smooth' })
      // }, 200)
    }
  }, [childId, fetchCampaignChildDetails])

  useEffect(() => {
    const foundCamp = phishSmsCampaignList.data?.find(
      c => c.campaignMainId.toString() === phishCampaignId?.toString(),
    )

    if (childId) {
      const campChildren = phishSmsCampaignChildren[phishCampaignId]

      if (foundCamp && campChildren) {
        const foundChildCamp = campChildren.find(
          c => c.campaignId.toString() === childId.toString(),
        )
        setShowChildDetails(foundChildCamp)
      } else {
        setShowChildDetails(false)
      }
    } else {
      setShowDetails(!!foundCamp)
    }
  }, [phishCampaignId, childId, phishSmsCampaignChildren, phishSmsCampaignList])

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Grid container spacing={2} alignItems="center" mb={4}>
            <Grid item md={10}>
              <FormCreator
                ref={form}
                sx={{ width: '80%' }}
                formItems={[
                  ...(canCreate
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<AddCircle />}
                              onClick={() => {
                                navigate('/phish_campaign_sms_create')
                              }}
                            >
                              {t('general.add_campaign')}
                            </Button>
                          ),
                        },
                      ]
                    : []),
                  {
                    label: t('general.custom_date_range'),
                    name: 'dateRangeFilter',
                    type: 'dateRange',
                    disabled: !isCustomRange,
                    defaultValue: { startDate: new Date(), endDate: new Date() },
                    inputConfig: {
                      onChange: value => {
                        setFilterDateRange(value)
                      },
                    },
                  },
                  {
                    label: t('general.quick_search'),
                    name: 'search',
                    defaultValue: '',
                    inputConfig: {
                      onSubmit: value => {
                        setSearch(value)
                      },
                    },
                  },
                  {
                    label: t('general.status_selection'),
                    name: 'status',
                    defaultValue: 'ALL',
                    type: 'select',
                    inputConfig: {
                      onChange: value => {
                        const val = value.target?.value || value
                        setSearchStatus(val)
                      },
                      options: [
                        { value: 'ALL', label: t('status.ALL') },
                        { value: 'NO_START', label: t('status.NS') },
                        { value: 'IN_PROGRESS', label: t('status.IP') },
                        { value: 'COMPLETE', label: t('status.COM') },
                        { value: 'CANCEL', label: t('status.CAN') },
                      ],
                    },
                  },
                  {
                    label: t('general.quick_filter'),
                    name: 'setDateRange',
                    type: 'radio',
                    defaultValue: 6,
                    inputConfig: {
                      onChange: _ => {
                        setSelectedDateOpt(_.target.value)
                      },
                      options: [
                        { value: 1, label: t('general.month_num', { num: 1 }) },
                        { value: 2, label: t('general.month_num', { num: 2 }) },
                        { value: 3, label: t('general.month_num', { num: 3 }) },
                        { value: 6, label: t('general.month_num', { num: 6 }) },
                        { value: 0, label: t('general.custom') },
                      ],
                    },
                  },
                  {
                    label: t('general.campaign_run_type'),
                    name: 'setRunTypeOpt',
                    type: 'checkboxList',
                    tooltip:
                      companyId === 1
                        ? t('tooltips.prompt_message_1')
                        : t('tooltips.prompt_message_2'),
                    defaultValue: ['main', 'pre'],
                    inputConfig: {
                      onChange: val => {
                        setRunTypeOpt(val)
                      },
                      options: [
                        { value: 'main', label: t('general.run_type_main') },
                        { value: 'pre', label: t('general.run_type_pre') },
                        ...(companyId === 1
                          ? [{ value: 'test', label: t('general.run_type_test') }]
                          : []),
                      ],
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />
            </Grid>
            <Grid item md={2}>
              <Stack direction="row">
                <Box flex={1} />
                <TableTabButton
                  selected={showChart}
                  onClick={() => {
                    setShowChart(true)
                  }}
                  label={t('general.bar_chart')}
                />
                <TableTabButton
                  selected={!showChart}
                  onClick={() => {
                    setShowChart(false)
                  }}
                  label={t('general.table')}
                />
              </Stack>
            </Grid>
          </Grid>

          {showChart ? (
            <CampaignGanttChart
              loading={isLoading}
              height={400}
              data={data || []}
              childrenData={phishSmsCampaignChildren || {}}
              statusTotal={phishSmsCampaignsStatusTotal}
              statusMap={{
                notStartNum: 'noStart',
                cancelNum: 'cancel',
                completeNum: 'complete',
                inProgressNum: 'inProgress',
              }}
              idKey="campaignMainId"
              nameKey="campaignMainName"
              startDateKey="startTime"
              endDateKey="endTime"
              childrenKey="children"
              childrenCompare={children => children === 1}
              selectedId={expandedId}
              onRowClick={handleRowClick}
              onChildRowClick={handleChildRowClick}
              onExpand={handleExpand}
              pagination={pagination}
              startDate={filterDateRange?.startDate}
              endDate={filterDateRange?.endDate}
            />
          ) : (
            <Table
              minHeight={400}
              columns={cols}
              rows={data || []}
              dataGridConfig={{
                sortingMode: 'server',
                sortingOrder: ['desc', 'asc', null],
                sortModel: sort,
                onSortModelChange: newSort => setSort(newSort),
                getRowId: row => row.campaignMainId,
                getRowHeight: () => 'auto',
                loading: isLoading,
              }}
              noRowsLabel={t('errors.no_campaign')}
              pagination={pagination}
            />
          )}
        </Grid>

        <Grid ref={detailsRef} item>
          {isDetailsLoading && (
            <Box
              sx={{ padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CircularProgress />
            </Box>
          )}
          {!isDetailsLoading && showDetails && phishCampaignId && !childId && (
            <PhishCampaignSmsDetails
              fetchCampaignDetails={() => {
                fetchCampaignDetails()
              }}
            />
          )}

          {!isDetailsLoading && showChildDetails && childId && (
            <PhishCampaignSmsChildDetails
              fetchCampaignDetails={() => {
                fetchCampaignChildDetails()
              }}
            />
          )}
        </Grid>
      </Grid>

      <ConfirmDialog
        open={!!deleteCampData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteCampData()
        }}
        onSubmit={() => {
          deletePhishCamp()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteCampData?.campaignMainName || deleteCampData?.campaignName}${t(
            'messages.info',
          )}?`}</Typography>
        </Stack>
      </ConfirmDialog>
    </Container>
  )
}

export default connect(mapStateToProps)(PhishCampaignSmsDashboard)
